<template>
  <div>
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('accounting_expense_transactions_filters') }}
          </h3>
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.keyword"
            :placeholder="$t('accounting_transactions_search')"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.status"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transactions_status')"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.payment_source"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="paymentSourceOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transactions_payment_source')"
            :loading="loadingAccounts || loadingWallets || loadingPayouts"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
            @input="onPaymentSourceChange"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.transaction_reason"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="transactionReasonOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transactions_transaction_reason')"
            :loading="loadingReasons"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
          />
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.date_from"
            type="date"
            :placeholder="$t('accounting_accounts_from_date')"
          />
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.date_to"
            type="date"
            :placeholder="$t('accounting_accounts_to_date')"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.source_id"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="sourceOptions"
            class="w-100 mb-0"
            :reduce="val => val.id"
            :get-option-label="getSourceOptionLabel"
            :placeholder="sourcePlaceholder"
            :loading="sourceLoading"
            clearable
          />
        </b-col>
      </b-row>
    </b-card>
    <b-card class="w-100 d-flex rounded-lg">
      <b-row>
        <b-col
          md="12"
          class="pb-2 w-100 d-flex justify-content-between"
        >
          <b-button
            v-if="hasPermission('create transactions')"
            variant="primary"
            @click="gotoCreateTransaction"
          >
            {{ $t('accounting_expense_transactions_add') }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="load"
        class="text-center"
        style="width: 100%;"
      >
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </div>

      <b-table
        v-else
        striped
        :items="items.data || items"
        :fields="fields"
        responsive
      >
        <template #cell(transaction_reason)="data">
          <span>
            {{ $i18n.locale === 'ar'
              ? (data.item.transaction_reason && data.item.transaction_reason.label_ar) || ''
              : (data.item.transaction_reason && data.item.transaction_reason.label) || '' }}
          </span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant((data.item.status && data.item.status.value) || '')">
            {{ $i18n.locale === 'ar' ? (data.item.status && data.item.status.label_ar) || '' : (data.item.status && data.item.status.label) || '' }}
          </b-badge>
        </template>
        <template #cell(amount)="data">
          {{ formatCurrency(data.item.amount) }}
        </template>
        <template #cell(fee)="data">
          {{ formatCurrency(data.item.fee || 0) }}
        </template>
        <template #cell(project)="data">
          {{ projectLabel(data.item) }}
        </template>
        <template #cell(invoice)="data">
          <b-link
            v-if="invoiceOf(data.item)"
            :href="invoiceOf(data.item).url || invoiceOf(data.item).download_url"
            target="_blank"
          >
            {{ invoiceOf(data.item).original_name || invoiceOf(data.item).file_name || $t('accounting_transactions_invoice') }}
          </b-link>
          <span v-else>—</span>
        </template>
        <template #cell(transaction_date)="data">
          {{ formatDate(data.item.transaction_date) }}
        </template>
        <template #cell(payment_source)="data">
          <span>
            {{ $i18n.locale === 'ar'
              ? (data.item.payment_source && data.item.payment_source.label_ar) || ''
              : (data.item.payment_source && data.item.payment_source.label) || '' }}
          </span>
        </template>
        <template #cell(source)="data">
          <span v-if="sourceText(data.item)">{{ sourceText(data.item) }}</span>
        </template>
        <template #cell(paid_from)="data">
          {{ paidFromText(data.item) }}
        </template>
        <template #cell(received_by)="data">
          {{ receivedByText(data.item) }}
        </template>
        <template #cell(actions)="data">
          <div
            v-if="data.item"
            class="d-flex align-items-center"
          >
            <b-link
              v-b-tooltip.hover.top="$t('accounting_transactions_view_transaction')"
              class="ml-1"
              :to="{ name: 'accounting-expense-transactions-view', params: { id: data.item.id} }"
            >
              <feather-icon
                icon="EyeIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('approve transactions')"
              v-b-tooltip.hover.top="$t('accounting_transactions_approve')"
              class="ml-1 text-success"
              @click="approveTransaction(data.item.id)"
            >
              <feather-icon
                icon="CheckIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('reject transactions')"
              v-b-tooltip.hover.top="$t('accounting_transactions_reject')"
              class="ml-1 text-danger"
              @click="rejectTransaction(data.item.id)"
            >
              <feather-icon
                icon="XIcon"
                size="18"
              />
            </b-link>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="items.meta"
        v-model="currentPage"
        align="center"
        :total-rows="items.meta.total"
        :per-page="items.meta.per_page"
        @change="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BCard,
  BRow,
  BCol,
  BBadge,
  BLink,
  VBTooltip,
  BSpinner,
  BPagination,
  BFormInput,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as transactionReasonsAPI from '@/api/accounting/transactionReasons'
import * as accountsAPI from '@/api/accounting/accounts'
import * as walletsAPI from '@/api/accounting/wallets'
import { invoiceMedia } from '@/utils/transactionMoney'
import {
  fetchActivePayouts, paidFromText, payoutOptionLabel,
  receivedByText, voucherSourceText, walletOptionLabel,
} from '@/utils/voucherSource'

export default {
  components: {
    BTable,
    BButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    BFormInput,
    vSelect,
    FeatherIcon,
    ToastificationContent,
  },
  computed: {
    ...mapGetters({
      items: 'accounting/expenseTransactions/items',
      load: 'accounting/expenseTransactions/load',
    }),
    sourceOptions() {
      if (this.filter.payment_source === 'wallet') return this.walletOptions
      if (this.filter.payment_source === 'employee_payment_account') return this.payoutOptions
      if (this.filter.payment_source === 'account') return this.accountOptions
      return []
    },
    sourcePlaceholder() {
      if (this.filter.payment_source === 'wallet') return this.$t('accounting_transactions_custody_wallet')
      if (this.filter.payment_source === 'employee_payment_account') return this.$t('accounting_transactions_employee_payment_account')
      if (this.filter.payment_source === 'account') return this.$t('accounting_transactions_company_account')
      return this.$t('accounting_transactions_source')
    },
    sourceLoading() {
      if (this.filter.payment_source === 'wallet') return this.loadingWallets
      if (this.filter.payment_source === 'account') return this.loadingAccounts
      if (this.filter.payment_source === 'employee_payment_account') return this.loadingPayouts
      return false
    },
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  watch: {
    'filter.keyword': function () {
      this.debounceSearch()
    },
    'filter.status': function () {
      this.loadTransactions()
    },
    'filter.payment_source': function () {
      this.filter.source_id = null // Reset source when payment source changes
      this.loadTransactions()
    },
    'filter.transaction_reason': function () {
      this.loadTransactions()
    },
    'filter.date_from': function () {
      this.loadTransactions()
    },
    'filter.date_to': function () {
      this.loadTransactions()
    },
    'filter.source_id': function () {
      this.loadTransactions()
    },
  },
  data() {
    return {
      currentPage: 1,
      loadingAccounts: false,
      loadingWallets: false,
      loadingPayouts: false,
      loadingReasons: false,
      filter: {
        keyword: null,
        status: null,
        payment_source: null,
        transaction_reason: null,
        source_id: null,
        date_from: null,
        date_to: null,
        per_page: 15,
        page: 1,
      },
      statusOptions: [
        { value: 'pending', label: 'Pending', label_ar: 'قيد الانتظار' },
        { value: 'approved', label: 'Approved', label_ar: 'موافق عليه' },
        { value: 'rejected', label: 'Rejected', label_ar: 'مرفوض' },
      ],
      paymentSourceOptions: [
        { value: 'account', label: 'Company till', label_ar: 'حساب الشركة' },
        { value: 'wallet', label: 'Custody wallet', label_ar: 'محفظة عهدة' },
        { value: 'employee_payment_account', label: 'Employee payment account', label_ar: 'حساب صرف الموظف' },
      ],
      transactionReasonOptions: [],
      accountOptions: [],
      walletOptions: [],
      payoutOptions: [],
      fields: [],
      searchTimeout: null,
    }
  },
  async mounted() {
    await Promise.all([
      this.loadTransactionReasons(),
      this.loadAccounts(),
      this.loadWallets(),
      this.loadPayouts(),
    ])
    this.init()
  },
  methods: {
    hasPermission(permission) {
      const userPermissions = this.$store.getters['users/user_permissions']
      if (Array.isArray(userPermissions)) {
        return userPermissions.includes(permission)
      }
      return Object.values(userPermissions || {}).includes(permission)
    },
    onPaymentSourceChange() {
      this.filter.source_id = null
    },
    sourceText(item) {
      return voucherSourceText(item, this.payoutOptions)
    },
    paidFromText,
    receivedByText,
    getSourceOptionLabel(option) {
      if (!option) return ''
      if (this.filter.payment_source === 'wallet') return walletOptionLabel(option)
      if (this.filter.payment_source === 'employee_payment_account') return payoutOptionLabel(option)
      return `${option.code} - ${option.name}`
    },
    gotoCreateTransaction() {
      this.$router.push({ name: 'accounting-expense-transactions-create' })
    },
    onPageChange(page) {
      this.currentPage = page
      this.filter.page = page
      this.loadTransactions()
    },
    loadTransactions() {
      const params = { ...this.filter }
      if (!params.keyword) delete params.keyword
      if (!params.status) delete params.status
      if (!params.payment_source) delete params.payment_source
      if (!params.transaction_reason) delete params.transaction_reason
      if (!params.source_id) delete params.source_id
      if (!params.date_from) delete params.date_from
      if (!params.date_to) delete params.date_to
      this.$store.dispatch('accounting/expenseTransactions/list', params)
    },
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.filter.page = 1
        this.currentPage = 1
        this.loadTransactions()
      }, 500)
    },
    invoiceOf(item) {
      return invoiceMedia(item)
    },
    projectLabel(item) {
      const project = item && item.project
      if (project && typeof project === 'object') {
        const name = project.name || ''
        const code = project.project_number || project.code || ''
        if (code && name) return `${code} — ${name}`
        return name || code
      }
      return item && item.project_id ? `#${item.project_id}` : ''
    },
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US')
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
      }
      return variants[status] || 'secondary'
    },
    async approveTransaction(id) {
      try {
        await this.$store.dispatch('accounting/expenseTransactions/approve', id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.approved_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransactions()
      } catch (error) {
        const errorMessage = error.response?.data?.message || this.$t('accounting_transactions_messages.error')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async rejectTransaction(id) {
      try {
        await this.$store.dispatch('accounting/expenseTransactions/reject', id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.rejected_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransactions()
      } catch (error) {
        const errorMessage = error.response?.data?.message || this.$t('accounting_transactions_messages.error')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async loadTransactionReasons() {
      this.loadingReasons = true
      try {
        const response = this.$i18n.locale === 'ar'
          ? await transactionReasonsAPI.getExpenseTransactionReasonOptionsAr()
          : await transactionReasonsAPI.getExpenseTransactionReasonOptions()
        this.transactionReasonOptions = response.data || []
      } catch (error) {
        console.error('Error loading transaction reasons:', error)
        this.transactionReasonOptions = []
      } finally {
        this.loadingReasons = false
      }
    },
    async loadAccounts() {
      this.loadingAccounts = true
      try {
        const response = await accountsAPI.getAccounts({ is_active: true, per_page: 1000 })
        this.accountOptions = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
        this.accountOptions = []
      } finally {
        this.loadingAccounts = false
      }
    },
    async loadPayouts() {
      this.loadingPayouts = true
      try {
        this.payoutOptions = await fetchActivePayouts()
      } catch (error) {
        console.error('Error loading payout accounts:', error)
        this.payoutOptions = []
      } finally {
        this.loadingPayouts = false
      }
    },
    async loadWallets() {
      this.loadingWallets = true
      try {
        const response = await walletsAPI.getWallets({ status: 'active', per_page: 1000 })
        this.walletOptions = response.data || []
      } catch (error) {
        console.error('Error loading wallets:', error)
        this.walletOptions = []
      } finally {
        this.loadingWallets = false
      }
    },
    init() {
      this.fields = [
        { key: 'transaction_number', label: this.$t('accounting_transactions_number'), sortable: true },
        { key: 'transaction_reason', label: this.$t('accounting_transactions_transaction_reason'), sortable: true },
        { key: 'amount', label: this.$t('accounting_transactions_amount'), sortable: true },
        { key: 'fee', label: this.$t('accounting_transactions_fee'), sortable: true },
        { key: 'invoice', label: this.$t('accounting_transactions_invoice') },
        { key: 'project', label: this.$t('accounting_transactions_project') },
        { key: 'payment_source', label: this.$t('accounting_transactions_payment_source') },
        { key: 'source', label: this.$t('accounting_transactions_source') },
        { key: 'paid_from', label: this.$t('accounting_paid_from') },
        { key: 'received_by', label: this.$t('accounting_received_by') },
        { key: 'transaction_date', label: this.$t('accounting_transactions_date'), sortable: true },
        { key: 'status', label: this.$t('accounting_transactions_status') },
        { key: 'actions', label: this.$t('accounting_accounts_actions') },
      ]
      this.loadTransactions()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
